import { FunctionComponent, ReactElement, useEffect, useState } from "react";
import BasicCard from "@components/cards/BasicCard";
import { EstimateResultSynthesis } from "@type/estimateResults/estimateResults.response";
import { ReactComponent as ProductionFarmer } from "src/static/images/production-farmer.svg";
import { ReactComponent as Message } from "src/static/images/message.svg";
import { ReactComponent as Calendar } from "src/static/icons/icon-calendar.svg";
import { ReactComponent as ArrowLeft } from "src/static/icons/icon-arrow-left.svg";
import { ReactComponent as Light } from "src/static/images/light.svg";
import BasicDivider from "@components/divider/BasicDivider";
import { useTranslation } from "react-i18next";
import InfoButton from "@components/buttons/InfoButton";
import HTMLReactParser from "html-react-parser";
import { toastInfo } from "@utils/toast-helper";
import InfoModal from "@components/modals/InfoModal";

interface ResultSynthesisProps {
  synthesis?: EstimateResultSynthesis;
  type: "farmer" | "collectivity";
  className?: string;
  onOpenFaq: (category: string) => any;
  onOpenTips: () => void;
}

const ResultSynthesis: FunctionComponent<ResultSynthesisProps> = ({
  synthesis,
  type,
  onOpenTips,
}) => {
  const { t } = useTranslation();
  const isEnough = synthesis?.isEnough ?? undefined;
  const color = isEnough ? "white" : "black";
  const [icpeStatus, setIcpeStatus] = useState<string | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const renderIcpeNotification = () => {
    if (icpeStatus && synthesis?.icpeStatus) {
      const previousValue = Number(
        t(`results.synthesis.farmer.icpe.${icpeStatus}.value`)
      );
      const newValue = Number(
        t(`results.synthesis.farmer.icpe.${synthesis?.icpeStatus}.value`)
      );
      if (previousValue < newValue)
        toastInfo(HTMLReactParser(t("results.toasts.info.icpe-increase")));
      else toastInfo(HTMLReactParser(t("results.toasts.info.icpe-decrease")));
    }
  };

  useEffect(() => {
    if (icpeStatus && synthesis?.icpeStatus !== icpeStatus)
      renderIcpeNotification();
    if (synthesis?.icpeStatus) setIcpeStatus(synthesis?.icpeStatus);
  }, [synthesis?.icpeStatus]);

  const renderEmptySynthesis = (): ReactElement => {
    return (
      <BasicCard className="result-synthesis result-empty">
        <>
          <div className="result-arrow-left">
            <ArrowLeft aria-hidden="true" focusable="false" />
          </div>
          <div className="d-flex align-items-stretch justify-content-center flex-wrap flex-xl-nowrap">
            <div className="d-flex align-items-center flex-grow-1 me-xl-4 mb-4 mb-xl-0">
              <ProductionFarmer
                color="white"
                stroke="white"
                aria-hidden="true"
                focusable="false"
              />
              <div className="d-flex flex-column ms-4">
                <div
                  className={`d-flex flex-column ${
                    type === "collectivity" ? "flex-column-reverse" : ""
                  }`}
                >
                  <div className="d-flex align-items-baseline flex-wrap">
                    <span className="text-white stat-value me-2">
                      {t(`results.synthesis.${type}.production.methaneValue`)}
                    </span>
                    <span className="text-white stat-unit font-weight-bold">
                      {HTMLReactParser(
                        t(
                          `result_units.${t(
                            `results.synthesis.${type}.production.methaneUnit`
                          )}`
                        )
                      )}
                    </span>
                  </div>
                  <div className="d-flex align-items-baseline flex-wrap">
                    <span className="text-white stat-value me-2">
                      {t(`results.synthesis.${type}.production.energyValue`)}
                    </span>
                    <span className="text-white stat-unit font-weight-bold">
                      {HTMLReactParser(
                        t(
                          `result_units.${t(
                            `results.synthesis.${type}.production.energyUnit`
                          )}`
                        )
                      )}
                    </span>
                  </div>
                </div>
                <div className="text-white stat-description mb-0">
                  {t(`results.synthesis.${type}.production.label`)}
                </div>
              </div>
            </div>
            <BasicCard className="result-synthesis-infos">
              <>
                <h4>{t("results.synthesis.empty.title")}</h4>
                <BasicCard
                  className="card-possibilities card-grey card-small mt-2"
                  role="link"
                  onClick={() => {
                    window.open(t("results.synthesis.empty.url"), "_blank");
                  }}
                >
                  <>
                    <div className="possibilities-icon">
                      <Light aria-hidden="true" focusable="false" />
                    </div>
                    <div className="text-xs">
                      {t("results.synthesis.empty.more")}
                    </div>
                  </>
                </BasicCard>
              </>
            </BasicCard>
          </div>
        </>
      </BasicCard>
    );
  };

  return synthesis ? (
    <BasicCard
      className={`result-synthesis card-primary ${
        synthesis
          ? isEnough
            ? "result-enough"
            : "result-not-enough"
          : "result-empty"
      }`}
    >
      <>
        <div className="d-flex align-items-stretch justify-content-center flex-wrap flex-xl-nowrap">
          <InfoModal
            visible={isModalVisible}
            onOk={() => setIsModalVisible(false)}
            onCancel={() => setIsModalVisible(false)}
            isBody
          >
            <div>
              <div className="mt-3 mb-0">
                {HTMLReactParser(
                  t("results.synthesis.farmer.icpe.explanation")
                )}
              </div>
            </div>
          </InfoModal>
          <div
            className={
              "d-flex flex-column justify-content-between flex-grow-1 me-xl-4 mb-4 mb-xl-0"
            }
          >
            <div className="d-flex align-items-center">
              <ProductionFarmer
                className={isEnough ? "icon-white" : "icon-black"}
                aria-hidden="true"
                focusable="false"
              />
              <div className="d-flex flex-column ms-4">
                <div
                  className={`d-flex flex-column ${
                    type === "collectivity" ? "flex-column-reverse" : ""
                  }`}
                >
                  <div className="d-flex align-items-baseline flex-wrap">
                    <span className={`text-${color} stat-value me-2`}>
                      {synthesis.methaneProduction.value[0].value ?? ""}
                    </span>
                    <span
                      className={`font-weight-bold text-${color} stat-unit`}
                    >
                      {" "}
                      {HTMLReactParser(
                        t(
                          `result_units.${synthesis.methaneProduction.value[0].unit}`,
                          synthesis.methaneProduction.value[0].unit
                        )
                      ) ?? ""}
                    </span>
                  </div>
                  <div className="d-flex align-items-baseline flex-wrap">
                    <span className={`text-${color} stat-value me-2`}>
                      {synthesis.energyProduction.value[0].value ?? ""}
                    </span>
                    <span
                      className={`font-weight-bold text-${color} stat-unit`}
                    >
                      {" "}
                      {HTMLReactParser(
                        t(
                          `result_units.${synthesis.energyProduction.value[0].unit}`,
                          synthesis.energyProduction.value[0].unit
                        )
                      ) ?? ""}
                    </span>
                  </div>
                </div>
                <div className={`text-${color} stat-description mb-0`}>
                  {t("results.synthesis.farmer.production.label")}
                </div>
              </div>
            </div>
            <div className={`text-${color} font-weight-bold mt-4`}>
              {t(
                `results.synthesis.${
                  synthesis.isEnough ? "enough" : "not-enough"
                }`
              )}
            </div>
            {type === "farmer" && !isEnough && (
              <a
                className="text-black mt-4 link"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    onOpenTips();
                  }
                }}
                onClick={() => {
                  onOpenTips();
                }}
              >
                Découvrez comment optimiser votre production
              </a>
            )}
            <div
              className={`d-flex align-items-center text-sm text-${color} mt-4`}
            >
              <span>
                {t(
                  `results.synthesis.${type}.reference-${
                    synthesis.isEnough ? "enough" : "not-enough"
                  }`
                )}
                &nbsp;
                <span className={!isEnough ? "font-weight-bold" : ""}>
                  {t(
                    `results.synthesis.${type}.value-${
                      synthesis.isEnough ? "enough" : "not-enough"
                    }.${
                      type === "collectivity" ? "energyValue" : "methaneValue"
                    }`
                  )}
                  {HTMLReactParser(
                    t(
                      `result_units.${t(
                        `results.synthesis.${type}.value-${
                          synthesis.isEnough ? "enough" : "not-enough"
                        }.${
                          type === "collectivity" ? "energyUnit" : "methaneUnit"
                        }`
                      )}`
                    )
                  )}
                </span>
                <span>
                  {t(`results.synthesis.${type}.reference-separator`)}
                </span>
                <span className={!isEnough ? "font-weight-bold" : ""}>
                  {t(
                    `results.synthesis.${type}.value-${
                      synthesis.isEnough ? "enough" : "not-enough"
                    }.${
                      type === "collectivity" ? "methaneValue" : "energyValue"
                    }`
                  )}
                  {HTMLReactParser(
                    t(
                      `result_units.${t(
                        `results.synthesis.${type}.value-${
                          synthesis.isEnough ? "enough" : "not-enough"
                        }.${
                          type === "collectivity" ? "methaneUnit" : "energyUnit"
                        }`
                      )}`
                    )
                  )}
                </span>
              </span>
            </div>
          </div>
          <BasicCard
            className={`result-synthesis-infos d-flex flex-column ${
              synthesis.icpeUrl
                ? "justify-content-between"
                : "justify-content-center"
            }`}
          >
            <>
              {type === "farmer" && !isEnough && (
                <>
                  <div className="d-flex flex-row align-items-center">
                    <Light
                      className="icon-black result-synthesis-infos-icon"
                      aria-hidden="true"
                      focusable="false"
                    />
                    <a
                      className="text-black ms-3 link h-auto text-sm font-weight-bold"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          onOpenTips();
                        }
                      }}
                      onClick={() => {
                        onOpenTips();
                      }}
                    >
                      Comment augmenter votre capacité de production ?
                    </a>
                  </div>
                  <BasicDivider height={1} className="w-100" variant="grey" />
                </>
              )}
              <div className="d-flex flex-row align-items-center">
                <Message
                  className="icon-black result-synthesis-infos-icon"
                  aria-hidden="true"
                  focusable="false"
                />
                <a
                  className="text-black ms-3 link h-auto text-sm font-weight-bold"
                  href={"https://projet-methanisation.grdf.fr/contact"}
                  target="_blank"
                >
                  En discuter avec l'expert biométhane de votre département
                </a>
              </div>
              {type === "farmer" && synthesis.icpeStatus && (
                <>
                  <BasicCard className="card-white card-small mt-3">
                    <>
                      <div className="d-flex flex-column align-items-start">
                        <div className="d-flex align-items-center mb-3">
                          <div className="text-sm font-weight-bold">
                            Votre régime ICPE
                          </div>
                          <InfoButton
                            className="text-primary ms-10"
                            onClick={() => setIsModalVisible(true)}
                          />
                        </div>
                        <div className="text-primary font-weight-bold">
                          {icpeStatus}
                        </div>
                        <div className="text-dark-grey text-xs">
                          {t(
                            `results.synthesis.farmer.icpe.${icpeStatus}.description`
                          )}
                        </div>
                        <BasicCard className="card-grey card-small w-100 mt-3">
                          <div className="d-flex flex-row align-items-center">
                            <Calendar
                              className="icon-black result-synthesis-infos-icon-sm"
                              aria-hidden="true"
                              focusable="false"
                            />
                            <a
                              className="text-black ms-2 link h-auto text-xs"
                              href={
                                "https://projet-methanisation.grdf.fr/cms-assets/2021/06/vue-etapes-projet-methanisation.pdf"
                              }
                              target="_blank"
                            >
                              Planning adapté à votre régime
                            </a>
                          </div>
                        </BasicCard>
                      </div>
                    </>
                  </BasicCard>
                </>
              )}
              {type === "collectivity" && isEnough && (
                <>
                  <BasicCard className="card-result-collectivity text-white card-small mt-3">
                    <>
                      <div className="d-flex align-items-start">
                        <div className="card-result-collectivity-icon">
                          <ProductionFarmer
                            className="icon-white"
                            aria-hidden="true"
                            focusable="false"
                          />
                        </div>
                        <div className="card-result-collectivity-description">
                          Vous pouvez également intégrer des déchets agricoles à
                          votre projet de méthanisation.{" "}
                          {synthesis?.projectProfile && (
                            <a
                              href="https://projet-methanisation.grdf.fr/sites-injection"
                              target="_blank"
                            >
                              Voir les sites en injection utilisant des
                              biodéchets.
                            </a>
                          )}
                        </div>
                      </div>
                    </>
                  </BasicCard>
                </>
              )}
            </>
          </BasicCard>
        </div>
      </>
    </BasicCard>
  ) : (
    renderEmptySynthesis()
  );
};

export default ResultSynthesis;
